import React, { useState, useEffect, useMemo } from "react";
import { CalendarCheck, X, Users, Check } from "lucide-react";
import { toast } from "sonner";
import { canAttendSession } from "../utils/masonicDegrees";

export const SessionModal = ({
  isOpen,
  onClose,
  onSubmit,
  members = []
}) => {
  const [sessionNumber, setSessionNumber] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [title, setTitle] = useState("Sessão Ordinária de Instrução");
  const [sessionType, setSessionType] = useState("Econômica");
  const [degreeRequired, setDegreeRequired] = useState("1 - Aprendiz");
  const [sessionStatus, setSessionStatus] = useState("realizada");
  const [agenda, setAgenda] = useState("Abertura dos trabalhos, leitura da ata, instruções do Grau e giro do Tronco de Beneficência.");
  const [visitors, setVisitors] = useState("");
  const [presentIds, setPresentIds] = useState([]);

  // Sempre que abrir o modal ou trocar o grau, marca por padrão todos os elegíveis
  useEffect(() => {
    if (!isOpen) return;
    const eligibleIds = (members || [])
      .filter((m) => canAttendSession(m.degree, degreeRequired))
      .map((m) => m.id);
    setPresentIds(eligibleIds);
  }, [isOpen, degreeRequired, members]);

  const sortedMembers = useMemo(() => {
    return [...(members || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
  }, [members]);

  if (!isOpen) return null;

  const toggleMember = (id) => {
    setPresentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const markAllEligible = () => {
    const ids = sortedMembers
      .filter((m) => canAttendSession(m.degree, degreeRequired))
      .map((m) => m.id);
    setPresentIds(ids);
  };

  const clearAll = () => setPresentIds([]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sessionNumber.trim()) {
      toast.error("Informe o número da sessão.");
      return;
    }

    onSubmit({
      session_number: sessionNumber.trim(),
      date,
      title: title.trim(),
      session_type: sessionType,
      degree_required: degreeRequired,
      trunk_amount: 0.0,
      agenda: agenda.trim(),
      visitors: visitors.trim(),
      present_member_ids: presentIds,
      justified_member_ids: [],
      status: sessionStatus,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div
        data-testid="modal-session-form"
        className="w-full max-w-2xl bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.25)] overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <CalendarCheck className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              Cadastrar Nova Sessão Litúrgica
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-close-session-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs text-slate-300 overflow-y-auto">

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Número da Sessão *</label>
              <input
                data-testid="input-session-number"
                type="text"
                required
                placeholder="Ex: 1.424"
                value={sessionNumber}
                onChange={(e) => setSessionNumber(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 font-mono text-sm"
              />
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Data da Sessão *</label>
              <input
                data-testid="input-session-date"
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Título / Assunto Principal</label>
            <input
              data-testid="input-session-title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold text-slate-300">Tipo de Sessão</label>
              <select
                data-testid="select-session-type"
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="Econômica">Econômica (Ordinária)</option>
                <option value="Magna de Iniciação">Magna de Iniciação</option>
                <option value="Magna de Elevação">Magna de Elevação</option>
                <option value="Magna de Exaltação">Magna de Exaltação</option>
                <option value="Banquete Ritualístico">Banquete Ritualístico</option>
                <option value="Instrução">Instrução Especial</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Grau Exigido</label>
              <select
                data-testid="select-session-degree"
                value={degreeRequired}
                onChange={(e) => setDegreeRequired(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="1 - Aprendiz">1º Grau - Aprendiz</option>
                <option value="2 - Companheiro">2º Grau - Companheiro</option>
                <option value="3 - Mestre">3º Grau - Mestre</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 font-semibold text-slate-300">Situação</label>
              <select
                data-testid="select-session-status"
                value={sessionStatus}
                onChange={(e) => setSessionStatus(e.target.value)}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="agendada">Agendada</option>
                <option value="realizada">Realizada</option>
                <option value="encerrada">Encerrada</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Pauta / Ordem do Dia</label>
            <textarea
              rows="2"
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-slate-300">Irmãos Visitantes</label>
            <input
              type="text"
              placeholder="Ex: Ir. João Silva (Loja Harmonia 104)"
              value={visitors}
              onChange={(e) => setVisitors(e.target.value)}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
            />
          </div>

          {/* Presenças da Loja */}
          <div className="pt-3 border-t border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <label className="font-semibold text-slate-200 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-[#D4AF37]" />
                Presença dos Irmãos ({presentIds.length} de {sortedMembers.length} marcados)
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={markAllEligible}
                  data-testid="btn-mark-all-eligible"
                  className="text-[10px] uppercase tracking-wider border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-2 py-1 rounded-lg font-bold"
                >
                  Marcar Elegíveis
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  data-testid="btn-clear-all"
                  className="text-[10px] uppercase tracking-wider border border-slate-700 text-slate-300 hover:bg-slate-800 px-2 py-1 rounded-lg font-bold"
                >
                  Limpar
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 italic mb-2">
              Todos os Irmãos aparecem abaixo. Os do grau <b>{degreeRequired}</b> e superiores vêm marcados por padrão (regra maçônica).
            </p>

            {sortedMembers.length === 0 ? (
              <div className="p-4 rounded-xl border border-dashed border-slate-700 text-center text-slate-500">
                Nenhum Irmão cadastrado ainda. Cadastre em Membros da Loja.
              </div>
            ) : (
              <div className="max-h-[220px] overflow-y-auto pr-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {sortedMembers.map((m) => {
                  const checked = presentIds.includes(m.id);
                  const eligible = canAttendSession(m.degree, degreeRequired);
                  return (
                    <label
                      key={m.id}
                      data-testid={`session-member-toggle-${m.id}`}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-all ${
                        checked
                          ? "bg-emerald-500/10 border-emerald-500/40"
                          : eligible
                          ? "bg-[#080C14] border-slate-800 hover:border-slate-600"
                          : "bg-[#080C14] border-slate-800 opacity-60"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMember(m.id)}
                        className="accent-emerald-500 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-white truncate">{m.name}</div>
                        <div className="text-[9px] text-slate-500 font-mono truncate">
                          CIM {m.cim} • {m.degree}
                          {!eligible && <span className="text-amber-400"> · fora do grau</span>}
                        </div>
                      </div>
                      {checked && <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="btn-submit-session"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              Registrar Sessão
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};
